import { useMemo, useState } from 'react';
import { Hourglass, Play, Plus } from '@phosphor-icons/react';
import { CoreState, coreStateToBooleans } from 'common/CommonConstants';
import { DeploymentDisableRequest, DeploymentEnableRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { trpc, useMutation } from 'client/lib/trpc';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { getFeaturesFromState } from 'client/components/Status/statusUtils';

const createStartOptions = (action: string) => [
  {
    id: 'one',
    title: `${action} 1 hour`,
    subtitle: 'Scheduled shut down',
    icon: action === 'Add' ? <Hourglass /> : <Play />,
    ornament: action === 'Add' ? <Plus weight="bold" /> : <Hourglass />,
  },
  {
    id: 'eight',
    title: `${action} 8 hours`,
    subtitle: 'Scheduled shut down',
    icon: action === 'Add' ? <Hourglass /> : <Play />,
    ornament: action === 'Add' ? <Plus weight="bold" /> : <Hourglass />,
  },
  {
    id: 'twelve',
    title: `${action} 12 hours`,
    subtitle: 'Scheduled shut down',
    icon: action === 'Add' ? <Hourglass /> : <Play />,
    ornament: action === 'Add' ? <Plus weight="bold" /> : <Hourglass />,
  },
  {
    id: 'forever',
    title: 'Run forever',
    icon: <Play />,
  },
];

const durationToRunHours = (duration: string | null) => {
  let runHours = null;
  if (duration === 'one') runHours = 1;
  if (duration === 'eight') runHours = 8;
  if (duration === 'twelve') runHours = 12;
  return runHours;
};

export const useDeploymentEnabledState = ({
  deploymentID = null,
  action = 'start',
}: {
  deploymentID: number | null | undefined;
  action?: 'start' | 'add';
}) => {
  const { setError } = useError();
  const statusTree = useDeploymentStatusTree(deploymentID);
  const combinedState = {
    deploymentStateID: statusTree?.deploymentStateID,
    coreStateID: statusTree?.core.coreStateID,
    itemStateID: statusTree?.itemStateID,
  };
  const stateFeatures = getFeaturesFromState(combinedState);

  const coreStateID = statusTree?.core.coreStateID || CoreState.DELETED;
  const timeRunUntil = statusTree?.core.timeRunUntil || null;
  const { isDeleted, isRunning, isLoading: isCoreLoading } = coreStateToBooleans(coreStateID);

  // FIXME: Limit the amount of running cores for "Free" accounts (for now)
  const [isCoreLimitDialogOpen, setIsCoreLimitDialogOpen] = useState(false);
  const [isNoCreditsDialogOpen, setIsNoCreditsDialogOpen] = useState(false);

  const addTimeMutation = useMutation(trpc.deployment.addTime, {});

  const startOptions = useMemo(() => createStartOptions(action === 'start' ? 'Run for' : 'Add'), [action]);

  const handleAddTime = (duration: string | null) => {
    if (!deploymentID) return;
    if (!isRunning) return;
    addTimeMutation.mutate({ deploymentID, runHours: durationToRunHours(duration) });
  };

  const handleToggle = async (duration: string | null) => {
    if (!deploymentID) return;
    if (!stateFeatures.isToggleable) return;

    const enabled = stateFeatures.isEnabled;

    const state = !enabled;
    if (state && !isDeleted) return;
    if (!state && !isRunning) return;

    const runHours = durationToRunHours(duration);

    let response;
    if (!enabled) {
      response = await Api.post<DeploymentEnableRequest>('/deployment/enable', { deploymentID, runHours });
      umami.track('deployment-enable', { deploymentID, runHours, status: response.status });
    } else {
      response = await Api.post<DeploymentDisableRequest>('/deployment/disable', { deploymentID });
      umami.track('deployment-disable', { deploymentID, status: response.status });
    }

    if (response.status !== 'ok') {
      if (response.status === 'core limit reached') {
        setIsCoreLimitDialogOpen(true);
      } else if (response.status === 'no credits') {
        setIsNoCreditsDialogOpen(true);
      } else {
        setError('Could not change deployment state', { status: response.status, errorKey: 'enable-deployment' });
      }
    }
  };

  return {
    enabled: stateFeatures.isEnabled,
    startOptions,
    timeRunUntil,
    isLoading: !stateFeatures.isToggleable || addTimeMutation.isLoading || isCoreLoading,
    isToggleable: stateFeatures.isToggleable,
    toggle: handleToggle,
    addTime: handleAddTime,
    isCoreLimitDialogOpen,
    setIsCoreLimitDialogOpen,
    isNoCreditsDialogOpen,
    setIsNoCreditsDialogOpen,
  };
};
